/**
 * Server-side pagination + query-state hook (Plans 6.2).
 *
 * Owns page/limit/sort/filter as a single object so the whole state is encoded
 * in the query key (cache correctness) and mapped to server offset/limit params.
 *
 * Returns the state, a setter for each dimension, and the server params object
 * to pass into the feature query factory.
 */
import { useCallback, useMemo, useState } from "react";

export type SortDir = "asc" | "desc";

export interface ServerListState<F extends Record<string, unknown> = Record<string, never>> {
  page: number;
  limit: number;
  sortBy?: string;
  sortDir?: SortDir;
  filters: F;
}

export interface ServerPaginationParams {
  page: number;
  limit: number;
  offset: number;
  sortBy?: string;
  sortDir?: SortDir;
}

const DEFAULT_LIMIT = 20;

export function useServerPagination<F extends Record<string, unknown> = Record<string, never>>(
  initial: Partial<ServerListState<F>> = {},
) {
  const [page, setPage] = useState(initial.page ?? 1);
  const [limit, setLimit] = useState(initial.limit ?? DEFAULT_LIMIT);
  const [sortBy, setSortBy] = useState<string | undefined>(initial.sortBy);
  const [sortDir, setSortDir] = useState<SortDir | undefined>(initial.sortDir);
  const [filters, setFilters] = useState<F>((initial.filters ?? {}) as F);

  // Changing any dimension resets to page 1 (server list must start fresh).
  const resetToFirstPage = useCallback(() => setPage(1), []);

  const changeLimit = useCallback((next: number) => {
    setLimit(next);
    setPage(1);
  }, []);

  const changeSort = useCallback((nextSortBy: string | undefined, nextDir: SortDir | undefined) => {
    setSortBy(nextSortBy);
    setSortDir(nextDir ?? "asc");
    setPage(1);
  }, []);

  const changeFilters = useCallback((next: F) => {
    setFilters(next);
    setPage(1);
  }, []);

  const params: ServerPaginationParams = useMemo(() => ({
    page,
    limit,
    offset: (page - 1) * limit,
    ...(sortBy ? { sortBy, sortDir: sortDir ?? "asc" } : {}),
  }), [page, limit, sortBy, sortDir]);

  return {
    page,
    limit,
    sortBy,
    sortDir,
    filters,
    params,
    setPage,
    changeLimit,
    changeSort,
    changeFilters,
    resetToFirstPage,
  };
}